import { Box, Card, CardContent, Typography } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Brush,
  Legend,
} from "recharts";
import { useGlobalMarketProductPriceChartContext } from "../../../context/global-market/GlobalMarketProductPriceChartContext";
import { ChartTooltip, COLORS } from "../../../ui/Theme";
import { ListDivider } from "../../../ui/ListStyles";
import useResponsive from "../../../components/hooks/useResponsive";
import NotFound from "../../../components/notfound/NotFound";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { x, y } = payload[0].payload;
    return (
      <ChartTooltip>
        <Typography sx={{ fontSize: "12px", fontWeight: 900 }}>{x}</Typography>
        <ListDivider flexItem />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: COLORS.BLUE,
          }}
        >
          <Typography sx={{ fontSize: "12px" }}>قیمت(دلار) : </Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 900 }}>
            {y}
          </Typography>
        </Box>
      </ChartTooltip>
    );
  }
  return null;
};

const GlobalMarketProductPriceChartChart = () => {
  const { chartData } = useGlobalMarketProductPriceChartContext();
  const { isMd } = useResponsive();
  const formattedData = chartData.map((item) => ({
    x: item.trade_date,
    y: item.avg_price,
  }));

  const totalDataLength = formattedData?.length || 0;
  const startIndex = Math.max(0, totalDataLength - (isMd ? 100 : 50));
  const endIndex = totalDataLength - 1;

  return (
    <Card
      sx={{
        border: "1px solid",
        borderColor: COLORS.SOFTGRAY,
        padding: 0,
        minHeight: 500,
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{ fontSize: { xs: "16px", md: "20px" }, fontWeight: 700 }}
        >
          قیمت کالا
        </Typography>
        {chartData.length < 1 ? (
          <Box sx={{ height: 550 }}>
            <NotFound />
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={550}>
            <LineChart
              style={{
                display: "flex",
                justifyContent: "center",
              }}
              data={formattedData}
              margin={{ top: 0, right: 0, bottom: 20, left: 0 }}
            >
              <CartesianGrid strokeDasharray="0 0" stroke={COLORS.SOFTGRAY} />
              <XAxis
                stroke={COLORS.GRAY}
                dataKey="x"
                angle={-25}
                textAnchor="middle"
                tickMargin={10}
                tickFormatter={(date) => {
                  const [year, month] = date.split("-");
                  return `${year}-${month}`;
                }}
              />
              <YAxis
                textAnchor="start"
                tickMargin={10}
                stroke={COLORS.GRAY}
                domain={[Math.min(...formattedData.map((d) => d.y)), "auto"]}
              />
              <Legend
                wrapperStyle={{
                  width: "fit-content",
                  top: -10,
                  right: 0,
                  lineHeight: "20px",
                }}
                verticalAlign="top"
                formatter={(value) => {
                  if (value === "y") {
                    return (
                      <span style={{ marginRight: "5px" }}>قیمت(دلار)</span>
                    );
                  }
                  return value;
                }}
              />

              <Tooltip cursor={{ fill: COLORS.SOFTGRAY }} content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="y"
                stroke={COLORS.BLUE}
                strokeWidth={2}
                dot={false}
              />

              <Brush
                dataKey="x"
                height={30}
                startIndex={startIndex}
                endIndex={endIndex}
                stroke={COLORS.BLUE}
                travellerWidth={10}
                y={520}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default GlobalMarketProductPriceChartChart;
